/**
 * Catalog API
 * Functions for fetching GPC hierarchy, categories, and product search
 */

import { coreAPI } from './client';
import type {
  GpcSegment,
  GpcHierarchyNode,
  GpcHierarchyWithPath,
  GpcBrickAttribute,
  SearchResponse,
  SearchProduct,
  CatalogSearchParams,
  PaginatedResponse,
} from '@/types/catalog';

/**
 * API service for catalog and GPC operations
 */
export const catalogAPI = {
  // ============================================================================
  // GPC Hierarchy
  // ============================================================================

  /**
   * Get all GPC segments (level 1)
   */
  async getGpcSegments(): Promise<PaginatedResponse<GpcSegment>> {
    return coreAPI.get<PaginatedResponse<GpcSegment>>('/catalog/gpc/segments');
  },

  /**
   * Get GPC hierarchy for a specific code (path + children)
   */
  async getGpcHierarchy(gpcCode: string): Promise<GpcHierarchyWithPath> {
    return coreAPI.get<GpcHierarchyWithPath>(`/catalog/gpc/hierarchy/${gpcCode}`);
  },

  /**
   * Get direct children of a GPC code
   */
  async getGpcChildren(gpcCode: string): Promise<PaginatedResponse<GpcHierarchyNode>> {
    return coreAPI.get<PaginatedResponse<GpcHierarchyNode>>(`/catalog/gpc/children/${gpcCode}`);
  },

  /**
   * Get brick attributes for a GPC brick (level 4)
   */
  async getGpcBrickAttributes(brickCode: string): Promise<PaginatedResponse<GpcBrickAttribute>> {
    return coreAPI.get<PaginatedResponse<GpcBrickAttribute>>(`/catalog/gpc/bricks/${brickCode}/attributes`);
  },

  /**
   * Search GPC nodes by name
   */
  async searchGpc(query: string, level?: number, limit = 20): Promise<PaginatedResponse<GpcHierarchyNode>> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    if (level) params.append('level', String(level));
    return coreAPI.get<PaginatedResponse<GpcHierarchyNode>>(`/catalog/gpc/search?${params}`);
  },

  // ============================================================================
  // Product Search
  // ============================================================================

  /**
   * Get products by GUIDs (batch endpoint for favorites display)
   *
   * @param productGuids - Array of platform product GUIDs
   * @returns Products found and list of not found GUIDs
   */
  async getProductsByGuids(productGuids: string[]): Promise<{
    products: SearchProduct[];
    not_found: string[];
  }> {
    // Handle empty array efficiently (no API call)
    if (productGuids.length === 0) {
      return { products: [], not_found: [] };
    }

    // API returns camelCase response
    interface ApiProduct {
      productGuid: string;
      merchantGuid: string;
      name: string;
      description?: string;
      brand?: { guid: string; name: string; slug: string };
      category?: { guid: string; name: string; path?: string; level?: number };
      price: number;
      compareAtPrice?: number;
      currency: string;
      stockStatus?: string;
      primaryImageUrl?: string;
      sku?: string;
      gtin?: string;
      mpn?: string;
      attributes?: Record<string, unknown>;
      createdAt?: string;
      publishedAt?: string;
    }

    interface ApiResponse {
      products: ApiProduct[];
      not_found: string[];
    }

    const raw = await coreAPI.post<ApiResponse>('/catalog/search/products/batch', {
      product_guids: productGuids,
    });

    // Transform camelCase to snake_case SearchProduct
    const products: SearchProduct[] = (raw.products || []).map((p): SearchProduct => ({
      product_guid: p.productGuid,
      merchant_guid: p.merchantGuid,
      name: p.name,
      description: p.description,
      brand: p.brand ? { guid: p.brand.guid, name: p.brand.name, slug: p.brand.slug } : undefined,
      category: p.category ? { guid: p.category.guid, name: p.category.name, path: p.category.path || '', level: p.category.level || 0 } : undefined,
      price: p.price,
      compare_at_price: p.compareAtPrice,
      currency: p.currency,
      stock_status: (p.stockStatus as SearchProduct['stock_status']) ?? 'in_stock',
      primary_image_url: p.primaryImageUrl,
      sku: p.sku || '',
      gtin: p.gtin,
      mpn: p.mpn,
      attributes: p.attributes || {},
      images: [],
      created_at: p.createdAt || new Date().toISOString(),
      updated_at: p.createdAt || new Date().toISOString(),
    }));

    return {
      products,
      not_found: raw.not_found || [],
    };
  },

  /**
   * Search products with GPC filtering
   */
  async searchProducts(params: CatalogSearchParams): Promise<SearchResponse> {
    const searchParams = new URLSearchParams();

    if (params.q) searchParams.append('q', params.q);
    if (params.page) searchParams.append('page', String(params.page));
    if (params.limit) searchParams.append('limit', String(params.limit));
    if (params.sort) searchParams.append('sort', params.sort);
    if (params.brand) searchParams.append('brand', params.brand);
    if (params.category) searchParams.append('category', params.category);
    if (params.gpc_code) searchParams.append('gpc_code', params.gpc_code);
    if (params.min_price !== undefined) searchParams.append('min_price', String(params.min_price));
    if (params.max_price !== undefined) searchParams.append('max_price', String(params.max_price));
    if (params.in_stock !== undefined) searchParams.append('in_stock', String(params.in_stock));

    // Add brick attribute filters
    if (params.attrs) {
      Object.entries(params.attrs).forEach(([key, values]) => {
        values.forEach((value) => {
          searchParams.append(`attr_${key}`, value);
        });
      });
    }

    // API returns camelCase response, transform to snake_case SearchResponse
    interface ApiProduct {
      productGuid: string;
      merchantGuid: string;
      modelGuid?: string;
      name: string;
      description?: string;
      brand?: { guid: string; name: string; slug: string };
      category?: { guid: string; name: string; path: string; level?: number };
      gpcBrickCode?: string;
      gpcSegmentCode?: string;
      gpcFamilyCode?: string;
      gpcClassCode?: string;
      gpcHierarchyPath?: string[];
      gpcBrickName?: string;
      brickAttributes?: Array<{ code: string; value_code: string; value_text: string }>;
      gtin?: string;
      mpn?: string;
      sku: string;
      attributes: Record<string, unknown>;
      price: number;
      compareAtPrice?: number;
      currency: string;
      stockStatus?: string;
      images?: Array<{ url: string; position: number; is_primary: boolean }>;
      primaryImageUrl?: string;
      createdAt: string;
      updatedAt?: string;
    }

    interface ApiResponse {
      products: ApiProduct[];
      pagination: { total: number; page: number; limit: number; totalPages: number };
    }

    const raw = await coreAPI.get<ApiResponse>(`/catalog/search/products?${searchParams}`, false);

    // Transform to SearchResponse with snake_case fields
    return {
      items: (raw.products || []).map((p): SearchProduct => ({
        product_guid: p.productGuid,
        merchant_guid: p.merchantGuid,
        model_guid: p.modelGuid,
        name: p.name,
        description: p.description,
        brand: p.brand ? { guid: p.brand.guid, name: p.brand.name, slug: p.brand.slug } : undefined,
        category: p.category ? { guid: p.category.guid, name: p.category.name, path: p.category.path, level: p.category.level || 0 } : undefined,
        gpc_brick_code: p.gpcBrickCode,
        gpc_segment_code: p.gpcSegmentCode,
        gpc_family_code: p.gpcFamilyCode,
        gpc_class_code: p.gpcClassCode,
        gpc_hierarchy_path: p.gpcHierarchyPath,
        gpc_brick_name: p.gpcBrickName,
        brick_attributes: p.brickAttributes,
        gtin: p.gtin,
        mpn: p.mpn,
        sku: p.sku || '',
        attributes: p.attributes || {},
        price: p.price,
        compare_at_price: p.compareAtPrice,
        currency: p.currency,
        stock_status: (p.stockStatus as SearchProduct['stock_status']) ?? 'in_stock',
        images: p.images || [],
        primary_image_url: p.primaryImageUrl,
        created_at: p.createdAt,
        updated_at: p.updatedAt || p.createdAt,
      })),
      total: raw.pagination?.total || 0,
      page: raw.pagination?.page || 1,
      limit: raw.pagination?.limit || 20,
    };
  },
};

export default catalogAPI;
